import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useState, useCallback } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useColors } from "@inbody/core";
import { useAuth } from "@inbody/keycloak";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ManualSelectDialog } from "./ManualSelectDialog.js";
function AccountMenu({
  showProfileHeader = false,
  showCopyright = false,
  extraItems,
  onOpenReleaseNotes,
  manuals
}) {
  var _a, _b, _c, _d, _e, _f, _g;
  const { t } = useTranslation("ui");
  const { user, logout, updateProfile } = useAuth();
  const { gray, brand } = useColors();
  const [anchorEl, setAnchorEl] = useState(null);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const open = Boolean(anchorEl);
  const hasManuals = !!manuals && manuals.length > 0;
  const handleOpenManuals = useCallback(() => {
    if (!manuals || manuals.length === 0) {
      return;
    }
    if (manuals.length === 1) {
      window.open(manuals[0].url, "_blank");
    } else {
      setManualDialogOpen(true);
    }
    setAnchorEl(null);
  }, [manuals]);
  const handleClickMenu = useCallback(
    (event) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );
  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);
  const handleLogoutButton = useCallback(() => {
    logout();
    setAnchorEl(null);
  }, [logout]);
  const handleUpdateProfile = useCallback(() => {
    updateProfile();
    setAnchorEl(null);
  }, [updateProfile]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      ButtonBase,
      {
        onClick: handleClickMenu,
        sx: {
          borderRadius: 1.5,
          padding: 0.75,
          "&:hover": {
            backgroundColor: gray[50]
          }
        },
        children: /* @__PURE__ */ jsx(
          Avatar,
          {
            sx: {
              width: 32,
              height: 32,
              background: `linear-gradient(135deg, ${brand[500]} 0%, ${brand[400]} 100%)`,
              fontSize: "14px",
              fontWeight: 600
            },
            children: (_a = user == null ? void 0 : user.firstName) == null ? void 0 : _a.toLocaleUpperCase().slice(0, 1)
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs(
      Popover,
      {
        anchorEl,
        open,
        onClose: handleCloseMenu,
        transformOrigin: { horizontal: "right", vertical: "top" },
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        slotProps: {
          paper: {
            sx: {
              borderRadius: 2,
              border: `1px solid ${gray[100]}`,
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              minWidth: 220,
              mt: 1,
              overflow: "hidden"
            }
          }
        },
        children: [
          showProfileHeader && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(Box, { sx: { px: 2.5, py: 2 }, children: /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", children: [
              /* @__PURE__ */ jsx(
                Avatar,
                {
                  sx: {
                    width: 40,
                    height: 40,
                    background: `linear-gradient(135deg, ${brand[500]} 0%, ${brand[400]} 100%)`,
                    fontSize: "14px",
                    fontWeight: 600
                  },
                  children: (_b = user == null ? void 0 : user.firstName) == null ? void 0 : _b.toLocaleUpperCase().slice(0, 1)
                }
              ),
              /* @__PURE__ */ jsxs(Box, { children: [
                /* @__PURE__ */ jsx(
                  Typography,
                  {
                    variant: "body2",
                    sx: { fontWeight: 600, color: gray[900] },
                    children: ((_c = user == null ? void 0 : user.attributes) == null ? void 0 : _c.preferredName) || (user == null ? void 0 : user.firstName) || ""
                  }
                ),
                /* @__PURE__ */ jsxs(Typography, { variant: "small", sx: { color: gray[500] }, children: [
                  (_d = user == null ? void 0 : user.attributes) == null ? void 0 : _d.jobTitle,
                  ((_e = user == null ? void 0 : user.attributes) == null ? void 0 : _e.jobTitle) && ((_f = user == null ? void 0 : user.attributes) == null ? void 0 : _f.jobPosition) ? " · " : "",
                  (_g = user == null ? void 0 : user.attributes) == null ? void 0 : _g.jobPosition
                ] })
              ] })
            ] }) }),
            /* @__PURE__ */ jsx(Divider, { sx: { borderColor: gray[100] } })
          ] }),
          /* @__PURE__ */ jsxs(Box, { sx: { py: 0.5 }, children: [
            /* @__PURE__ */ jsxs(
              MenuItem,
              {
                onClick: handleUpdateProfile,
                sx: {
                  px: 2.5,
                  py: 1.5,
                  color: gray[700],
                  "&:hover": {
                    backgroundColor: gray[50],
                    color: brand[500]
                  }
                },
                children: [
                  /* @__PURE__ */ jsx(
                    ListItemIcon,
                    {
                      sx: {
                        minWidth: 28,
                        color: "inherit",
                        "& svg": { fontSize: "18px" }
                      },
                      children: /* @__PURE__ */ jsx(PersonOutlineIcon, {})
                    }
                  ),
                  /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontWeight: 500 }, children: t("프로필 수정") })
                ]
              }
            ),
            hasManuals && /* @__PURE__ */ jsxs(
              MenuItem,
              {
                onClick: handleOpenManuals,
                sx: {
                  px: 2.5,
                  py: 1.5,
                  color: gray[700],
                  "&:hover": {
                    backgroundColor: gray[50],
                    color: brand[500]
                  }
                },
                children: [
                  /* @__PURE__ */ jsx(
                    ListItemIcon,
                    {
                      sx: {
                        minWidth: 28,
                        color: "inherit",
                        "& svg": { fontSize: "18px" }
                      },
                      children: /* @__PURE__ */ jsx(MenuBookOutlinedIcon, {})
                    }
                  ),
                  /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontWeight: 500 }, children: t("매뉴얼") })
                ]
              }
            ),
            onOpenReleaseNotes && /* @__PURE__ */ jsxs(
              MenuItem,
              {
                onClick: () => {
                  onOpenReleaseNotes();
                  setAnchorEl(null);
                },
                sx: {
                  px: 2.5,
                  py: 1.5,
                  color: gray[700],
                  "&:hover": {
                    backgroundColor: gray[50],
                    color: brand[500]
                  }
                },
                children: [
                  /* @__PURE__ */ jsx(
                    ListItemIcon,
                    {
                      sx: {
                        minWidth: 28,
                        color: "inherit",
                        "& svg": { fontSize: "18px" }
                      },
                      children: /* @__PURE__ */ jsx(CampaignOutlinedIcon, {})
                    }
                  ),
                  /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontWeight: 500 }, children: t("릴리즈 노트") })
                ]
              }
            ),
            extraItems == null ? void 0 : extraItems.map((item, idx) => /* @__PURE__ */ jsxs(
              MenuItem,
              {
                onClick: () => {
                  item.onClick();
                  setAnchorEl(null);
                },
                sx: {
                  px: 2.5,
                  py: 1.5,
                  color: gray[700],
                  "&:hover": {
                    backgroundColor: gray[50],
                    color: brand[500]
                  }
                },
                children: [
                  /* @__PURE__ */ jsx(
                    ListItemIcon,
                    {
                      sx: {
                        minWidth: 28,
                        color: "inherit",
                        "& svg": { fontSize: "18px" }
                      },
                      children: item.icon
                    }
                  ),
                  /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontWeight: 500 }, children: item.label })
                ]
              },
              idx
            )),
            /* @__PURE__ */ jsxs(
              MenuItem,
              {
                onClick: handleLogoutButton,
                sx: {
                  px: 2.5,
                  py: 1.5,
                  color: gray[700],
                  "&:hover": {
                    backgroundColor: brand[25],
                    color: brand[500]
                  }
                },
                children: [
                  /* @__PURE__ */ jsx(
                    ListItemIcon,
                    {
                      sx: {
                        minWidth: 28,
                        color: "inherit",
                        "& svg": { fontSize: "18px" }
                      },
                      children: /* @__PURE__ */ jsx(LogoutIcon, {})
                    }
                  ),
                  /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontWeight: 500 }, children: t("로그아웃") })
                ]
              }
            )
          ] }),
          showCopyright && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(Divider, { sx: { borderColor: gray[100] } }),
            /* @__PURE__ */ jsx(Box, { sx: { px: 2.5, py: 1.5, textAlign: "center" }, children: /* @__PURE__ */ jsxs(Typography, { variant: "small", sx: { color: gray[300] }, children: [
              "© ",
              dayjs().year(),
              " InBody CO., LTD."
            ] }) })
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      ManualSelectDialog,
      {
        open: manualDialogOpen,
        onClose: () => setManualDialogOpen(false),
        manuals: manuals ?? []
      }
    )
  ] });
}
export {
  AccountMenu
};
